import React from "react";

import Box from "components/common/Box";
import Text from "components/common/Text";
import Image from "components/common/Image";
import IconButton from "components/common/IconButton";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";

const DEFAULT_AUTHOR = "Shreebhashyam Sriniwas";

const MarketPulseHistoricalCard = ({
  pulse,
  onClick,
  isNew = false,
}) => (
  <Box
    onClick={onClick}
    role="button"
    tabIndex={0}
    onKeyDown={(event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onClick();
      }
    }}
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 2,
      width: "100%",
      p: { xs: 2, sm: 2.5, md: 3 },
      borderRadius: { xs: "14px", md: "16px" },
      backgroundColor: "#FFFFFF",
      border: "1px solid #E8ECF0",
      boxShadow: "0 1px 6px rgba(15, 23, 42, 0.04)",
      cursor: "pointer",
      transition:
        "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
      "&:hover": {
        transform: "translateY(-1px)",
        boxShadow: "0 4px 14px rgba(15, 23, 42, 0.07)",
        borderColor: "#D1D9E0",
      },
    }}
  >
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 1 }}>
        <Text sx={{ fontWeight: 700, fontSize: { xs: "16px", sm: "18px" }, color: "#111827" }}>
          {pulse.title || pulse.market}
        </Text>
        {isNew ? (
          <Box
            sx={{
              px: 1,
              py: 0.25,
              borderRadius: "999px",
              backgroundColor: "#DBEAFE",
              border: "1px solid #BFDBFE",
            }}
          >
            <Text sx={{ fontSize: "11px", fontWeight: 700, color: "#1D4ED8" }}>
              New
            </Text>
          </Box>
        ) : null}
        {pulse.extraMediaCount > 0 ? (
          <Box
            sx={{
              px: 1,
              py: 0.25,
              borderRadius: "999px",
              backgroundColor: "#EEF4FF",
              border: "1px solid #D6E4FF",
            }}
          >
            <Text sx={{ fontSize: "11px", fontWeight: 600, color: "#2B70C5" }}>
              +{pulse.extraMediaCount} more
            </Text>
          </Box>
        ) : null}
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: { xs: 1.25, sm: 2 },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Box
            sx={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              backgroundColor: "#22C55E",
              flexShrink: 0,
            }}
          />
          <Text sx={{ fontSize: "13px", color: "#374151", fontWeight: 500 }}>
            {pulse.author || DEFAULT_AUTHOR}
          </Text>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <CalendarTodayOutlinedIcon sx={{ fontSize: 14, color: "#9CA3AF" }} />
          <Text sx={{ fontSize: "13px", color: "#6B7280" }}>{pulse.listDate}</Text>
        </Box>

        {pulse.duration ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <AccessTimeOutlinedIcon sx={{ fontSize: 14, color: "#9CA3AF" }} />
            <Text sx={{ fontSize: "13px", color: "#6B7280" }}>{pulse.duration}</Text>
          </Box>
        ) : null}
      </Box>
    </Box>

    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
      {pulse.images?.[0]?.url ? (
        <Box
          sx={{
            display: { xs: "none", md: "block" },
            width: 132,
            height: 84,
            borderRadius: "12px",
            overflow: "hidden",
            border: "1px solid #E5E7EB",
            backgroundColor: "#F8FAFC",
          }}
        >
          <Image
            src={pulse.images[0].url}
            alt={pulse.title || pulse.market || "Market pulse chart"}
            objectFit="cover"
            height="100%"
            rootStyle={{ width: "100%", height: "100%" }}
            imgStyle={{ width: "100%", height: "100%", display: "block" }}
          />
        </Box>
      ) : null}

      <IconButton
        sx={{
          width: 40,
          height: 40,
          border: "1px solid #E5E7EB",
          backgroundColor: "#FFFFFF",
          flexShrink: 0,
          "&:hover": { backgroundColor: "#F9FAFB" },
        }}
        aria-label={`Open ${pulse.title || pulse.market} market pulse`}
      >
        <ChevronRightIcon sx={{ color: "#6B7280" }} />
      </IconButton>
    </Box>
  </Box>
);

export default MarketPulseHistoricalCard;
